import React, { createContext, useContext, ReactNode } from 'react';
import {
  DatadogTrackingOptions,
  DatadogTrackingProps,
  generateDatadogTrackingProps,
} from 'toolkit/environment/environment/userMonitoring.util';

/**
 * Context API for generating hierarchical Datadog action names.
 */
export interface TrackingContextValue {
  generateTrackingProps: (
    actionName: string,
    options?: DatadogTrackingOptions
  ) => DatadogTrackingProps;
  getFullContextName: () => string;
}

const TrackingContext = createContext<TrackingContextValue | null>(null);

interface TrackingProviderProps {
  children: ReactNode;
  contextName: string;
}

/**
 * Adds a stable feature segment to descendant Datadog action names.
 * @param children - Descendant UI that uses tracking.
 * @param contextName - Stable kebab-case context segment.
 * @returns Tracking context provider.
 */
export const TrackingProvider: React.FC<TrackingProviderProps> = ({
  children,
  contextName,
}) => {
  const parentContext = useContext(TrackingContext);

  const getFullContextName = (): string => {
    if (parentContext) {
      return `${parentContext.getFullContextName()}.${contextName}`;
    }
    return contextName;
  };

  const generateTrackingProps = (
    actionName: string,
    options?: DatadogTrackingOptions
  ): DatadogTrackingProps => {
    const fullContextName = getFullContextName();
    const fullActionName = `${fullContextName}.${actionName}`;

    return generateDatadogTrackingProps(fullActionName, options);
  };

  const contextValue: TrackingContextValue = {
    generateTrackingProps,
    getFullContextName,
  };

  return (
    <TrackingContext.Provider value={contextValue}>
      {children}
    </TrackingContext.Provider>
  );
};

/**
 * Returns contextual tracking helpers with a safe unscoped fallback.
 * @returns Datadog tracking helpers for the current React subtree.
 */
export const useTracking = (): TrackingContextValue => {
  const context = useContext(TrackingContext);
  if (!context) {
    return {
      generateTrackingProps: generateDatadogTrackingProps,
      getFullContextName: () => 'no-context',
    };
  }
  return context;
};
